/**
 * Reducer-side entry point for the swept brush: fold a `brush.stroke` payload onto a target.
 *
 * Segment i of the stroke is the crescent from s[i-1] to s[i] (see sweep.ts); it is a full sphere
 * when i == 0 and no `prev` is carried, or when i is listed in `breaks`. Each verb's per-cell rule is
 * idempotent (place: set value; subtract: delete; fill: set if empty; subtractIf: delete if the cell
 * holds the scope type; repaintIf: set value if the cell holds the scope type), so the fold is a set
 * union and splitting the sample list anywhere with `prev` carried over yields the same cells
 * (`combineBrushEvents` relies on this and asserts it in dev).
 *
 * Scope (materials-perf-plan B5): `subtractIf` and `repaintIf` carry `onlyValue`, the voxel under the
 * stroke's first hit, resolved by the controller and stored in the event so replay is deterministic.
 * "This material" means the same canonical type as `onlyValue` (see `WriteScope` in layerSystem.ts).
 *
 * Lump (materials-perf-plan B4): `lumpFx > 0` switches the segment walk to the banded sweep
 * (sweep.ts / noise.ts). The per-cell rule stays monotone in distance, so the crescent decomposition
 * and the split invariant hold unchanged; the noise is pure integer and seeded from the snapshot.
 *
 * Protection (`keep`): canonical types the erase verbs (`subtract`, `subtractIf`) never remove, stored
 * in the snapshot so replay is deterministic. The rule stays a per-cell idempotent delete, so the
 * split invariant holds. Absent or empty: nothing is protected (every stroke before this field).
 *
 * Protection also applies to the drawing verbs that overwrite (`place`, and `coat`'s recolouring): a
 * cell of a kept type is never replaced, so paint can be laid over a canvas without eating into it.
 *
 * Coat (`verb: 'coat'`, `coatFx` thickness): the brush lays a shell of paint of fixed thickness on the
 * surface under it instead of filling its whole sphere. Inside the swept volume, an empty cell is
 * filled when it lies within `coatFx` of a cell that was occupied *before the stroke*, and (with
 * `coatRepaint`) an occupied cell that was exposed before the stroke is recoloured (never a kept type).
 * So paint follows any surface (tops, sides, undersides), builds up stroke by stroke, and once the
 * room runs out (the document bounds) a new stroke still recolours the top layer. The rule reads a
 * *reference*: the document before the stroke (`BrushStrokeCtx.reference`; the reducer passes the
 * draft base while a stroke is being drawn). Without one, the fold reads the target itself before
 * writing anything (all spans are collected first), which equals the reference for a whole stroke in
 * one event. Either way the per-cell rule depends only on the reference, so the split invariant holds.
 *
 * Level (`verb: 'level'`): pour to a level. Every sample of the stroke sits at the same height, the
 * stroke's level (the controller picks it at the stroke's start: the surface plus a thickness). The
 * footprint is the swept shape's horizontal slice at that height; in each footprint column, empty cells
 * are filled from the level downward until the first occupied cell. A column already filled to the
 * level takes nothing more, so overlapping strokes (and a stroke crossing itself) stay level instead
 * of terracing, and paint already above the level is left alone. The per-column rule is idempotent and
 * reads only what it writes below the level, so the split invariant holds without a reference.
 *
 * Mirrors: this module applies them itself by reflecting clipped spans (mirrorSpans.ts) and writing
 * through the *plain* writers (`writePlace`/`writeFill`/`writeSubtract` and their scoped variants).
 * It deliberately does not use `writeFn`, which would consult the reducer's active mirror context and
 * reflect every cell a second time. That is the no-double-apply argument: exactly one of the two
 * mirroring mechanisms is on this path, and it is the span one.
 */

import { FX_ONE, type Fx3 } from './fixed'
import { forEachSegmentSpans, forEachSegmentSpansBanded, forEachSegmentSpansTapered, resolveLumpParams, type CellClip } from './sweep'
import { clipSpan, forEachReflectedSpanPlanned, planMirrors, type Bounds, type MirrorSpec } from './mirrorSpans'
import type { VoxelData, VoxelMap } from '@/voxelTypes'
import { posKeyXYZ } from '@/voxelMath'
import {
  isKept,
  writeFill,
  writePlace,
  writeRepaintIf,
  writeSubtract,
  writeSubtractIf,
  writeSubtractKeeping,
  type KeepTypes,
  type WriteScope,
} from '@/events/layerSystem'
import { canonicalVoxelType } from '@/voxels/registry'
import type { ChunkedVoxelGrid, SpanOp } from '@/storage/grid/ChunkedVoxelGrid'

export type BrushVerb = 'place' | 'subtract' | 'fill' | 'subtractIf' | 'repaintIf' | 'coat' | 'level'

/** The write scope of a brush: cells of this canonical type only. */
export type BrushScope = WriteScope

/** Everything the reducer needs to know about the brush; deep-equal snapshots coalesce. */
export type BrushSnapshot = {
  v: 1
  verb: BrushVerb
  value?: VoxelData
  rFx: number
  /** Scope of `subtractIf` / `repaintIf`: the voxel under the first hit; its canonical type is the scope. */
  onlyValue?: VoxelData
  /**
   * Lumpy shell (B4, noise.ts): per-cell effective radius in [rFx - lumpFx, rFx]. Absent or 0 means a
   * plain sweep. `lumpCell` is the noise lattice pitch in voxels; `seed` is chosen once per stroke by
   * the controller and stored here so replay is deterministic.
   */
  lumpFx?: number
  lumpCell?: number
  seed?: number
  /**
   * Protected canonical types (sorted, unique; `normalizeKeepTypes`): the erase verbs never remove a
   * cell of these types, and `place` / `coat` never overwrite one. Ignored by `fill` and `repaintIf`.
   * Absent: nothing is protected.
   */
  keep?: string[]
  /**
   * Erase verbs only: a floor. Cells below it (y < floorY) are never removed, a dig's bedrock.
   * Absent: the brush reaches the bottom of the world.
   */
  floorY?: number
  /** `coat` only: shell thickness in fixed point (cells within this distance of the old surface); 0 paints only (recolours, adds nothing). */
  coatFx?: number
  /** `coat` only: also recolour the exposed surface under the brush (what keeps a full canvas paintable). */
  coatRepaint?: boolean
}

/** A protected-type list in snapshot form: canonical, unique, sorted; undefined when empty. */
export function normalizeKeepTypes(types: Iterable<string> | null | undefined): string[] | undefined {
  if (!types) return undefined
  const out = new Set<string>()
  for (const t of types) if (typeof t === 'string' && t.length > 0) out.add(canonicalVoxelType(t))
  return out.size > 0 ? [...out].sort() : undefined
}

function isEraseVerb(verb: BrushVerb): boolean {
  return verb === 'subtract' || verb === 'subtractIf'
}

function honoursKeep(verb: BrushVerb): boolean {
  return isEraseVerb(verb) || verb === 'place' || verb === 'coat'
}

/** The protected set a snapshot erases or draws around, or null (`fill` / `repaintIf`, or nothing protected). */
export function brushKeepOf(brush: BrushSnapshot): KeepTypes | null {
  if (!honoursKeep(brush.verb) || !brush.keep || brush.keep.length === 0) return null
  return new Set(brush.keep.map(canonicalVoxelType))
}

/**
 * Would this brush remove `voxel` if it covered its cell? (Erase verbs only: the scope and the
 * protected types decide; drawing verbs never remove.) For callers that track what a stroke dug.
 */
export function brushRemovesVoxel(brush: BrushSnapshot, voxel: VoxelData | undefined, keep: KeepTypes | null = brushKeepOf(brush)): boolean {
  if (!voxel || !isEraseVerb(brush.verb)) return false
  if (keep && isKept(voxel, keep)) return false
  if (brush.verb === 'subtractIf') {
    const scope = brushScopeOf(brush)
    return !!scope && canonicalVoxelType(voxel.type) === scope.onlyType
  }
  return true
}

export function brushVerbIsScoped(verb: BrushVerb): boolean {
  return verb === 'subtractIf' || verb === 'repaintIf'
}

export function brushVerbNeedsValue(verb: BrushVerb): boolean {
  return verb !== 'subtract' && verb !== 'subtractIf'
}

/** The scope a snapshot writes with, or null when its verb is unscoped or the scope is missing. */
export function brushScopeOf(brush: BrushSnapshot): BrushScope | null {
  if (!brushVerbIsScoped(brush.verb) || !brush.onlyValue) return null
  return { onlyType: canonicalVoxelType(brush.onlyValue.type) }
}

export type BrushStrokePayload = {
  brush: BrushSnapshot
  /** Flat fixed-point sample centres: [x0, y0, z0, x1, y1, z1, ...]. */
  samples: number[]
  /** Fixed-point centre of the sample preceding samples[0] (so segment 0 is a crescent, not a sphere). */
  prev?: number[]
  /** Sample indices that start a fresh sphere (pointer jumped too far). */
  breaks?: number[]
  /**
   * Optional per-sample radii (fixed point, one per sample), for strokes whose width varies along
   * their length (a nib: `ScribbleSettings.nib`). Each is clamped to [0, brush.rFx], so `rFx` stays
   * the stroke's maximum radius and its bounds. Segments become round cones (sweep.ts
   * `forEachSegmentSpansTapered`). A stroke with radii ignores the brush's lump fields. Absent: every
   * sample has radius `rFx` (all strokes before this field existed).
   */
  radii?: number[]
  /** Radius of `prev` when `radii` is set (defaults to `rFx`). */
  prevR?: number
}

/** The radius of sample `i`, clamped to the brush's maximum. */
export function sampleRadiusFx(payload: BrushStrokePayload, i: number): number {
  const rFx = payload.brush.rFx
  const r = payload.radii?.[i]
  return clampRadiusFx(r, rFx)
}

function clampRadiusFx(r: number | undefined, rFx: number): number {
  if (r == null || !Number.isFinite(r)) return rFx
  return Math.max(0, Math.min(rFx, Math.round(r)))
}

/**
 * A write sink for row spans. `writeSpanX` writes cells (x0..x1, y, z) inclusive with `verb` and
 * returns the number of cells visited. Spans are already clipped to the document bounds. `scope` is
 * set exactly for the scoped verbs (`subtractIf`, `repaintIf`); `keep` only for erase verbs whose
 * snapshot protects some types (cells of those types are left alone).
 */
export type BrushTarget = {
  writeSpanX(
    y: number, z: number, x0: number, x1: number,
    verb: BrushVerb, value: VoxelData | undefined, scope?: BrushScope, keep?: KeepTypes,
  ): number
}

/** Read access to cells (a `VoxelMap` qualifies). */
export type CellReader = { get(key: number): VoxelData | undefined }

export type BrushStrokeCtx = {
  bounds: Bounds
  mirrors?: MirrorSpec | null
  /**
   * `coat` only: the document as it was before the stroke. Absent: the target is read (it must then
   * implement `CellReader`), before any write of this fold.
   */
  reference?: CellReader | null
}

export function sampleAt(samples: readonly number[], i: number): Fx3 {
  const o = i * 3
  return [samples[o], samples[o + 1], samples[o + 2]]
}

export function sampleCount(payload: BrushStrokePayload): number {
  return (payload.samples.length / 3) | 0
}

/** Fold the stroke onto `target`. Returns the number of cells visited (reflections included). */
export function applyBrushStroke(target: BrushTarget, payload: BrushStrokePayload, ctx: BrushStrokeCtx): number {
  const { brush } = payload
  const rFx = brush.rFx
  const verb = brush.verb
  const value = brush.value
  if (brushVerbNeedsValue(verb) && !value) return 0
  const scope = brushScopeOf(brush) ?? undefined
  // A scoped verb without its scope is malformed: writing nothing keeps replay deterministic.
  if (brushVerbIsScoped(verb) && !scope) return 0
  const keep = brushKeepOf(brush) ?? undefined
  const bounds = ctx.bounds
  const plan = planMirrors(ctx.mirrors)
  const breaks = payload.breaks && payload.breaks.length ? new Set(payload.breaks) : null
  const lump = resolveLumpParams(rFx, brush.lumpFx, brush.lumpCell, brush.seed)
  const n = sampleCount(payload)
  let visited = 0
  // Spans outside the bounds are clipped below anyway; skipping their rows up front keeps big spheres
  // in shallow documents cheap without changing what is written.
  const clip: CellClip = { x0: 0, x1: bounds.x - 1, y0: 0, y1: bounds.y - 1, z0: 0, z1: bounds.z - 1 }
  // Level: only the slice at the stroke's level (the first sample's height) is the footprint.
  if (verb === 'level' && n > 0) {
    const levelY = Math.round(payload.samples[1] / FX_ONE)
    if (levelY < 0 || levelY >= bounds.y) return 0
    clip.y0 = levelY
    clip.y1 = levelY
  }
  // An erase with a floor never reaches below it (reflected spans included).
  const floorY = isEraseVerb(verb) && brush.floorY !== undefined && brush.floorY > 0 ? brush.floorY : 0
  if (floorY > clip.y0) clip.y0 = floorY
  if (clip.y0 > clip.y1) return 0

  // Coat collects its spans (reflections included) and applies the shell rule after the walk.
  const coatSpans: number[] | null = verb === 'coat' || verb === 'level' ? [] : null
  const write = coatSpans
    ? (y: number, z: number, x0: number, x1: number) => { coatSpans.push(y, z, x0, x1) }
    : (y: number, z: number, x0: number, x1: number) => { if (y >= floorY) visited += target.writeSpanX(y, z, x0, x1, verb, value, scope, keep) }
  const emit = (y: number, z: number, x0: number, x1: number) => {
    const clipped = clipSpan(y, z, x0, x1, bounds)
    if (!clipped) return
    write(y, z, clipped[0], clipped[1])
    if (plan) forEachReflectedSpanPlanned(plan, y, z, clipped[0], clipped[1], bounds, write)
  }
  const finish = () => !coatSpans ? visited
    : verb === 'level' ? applyLevel(target, coatSpans, value)
    : applyCoat(target, coatSpans, brush, bounds, keep, ctx.reference)

  let prev: Fx3 | null = payload.prev && payload.prev.length >= 3
    ? [payload.prev[0], payload.prev[1], payload.prev[2]]
    : null
  if (payload.radii) {
    let prevR = clampRadiusFx(payload.prevR, rFx)
    for (let i = 0; i < n; i++) {
      const cur = sampleAt(payload.samples, i)
      const r = sampleRadiusFx(payload, i)
      const segPrev = breaks && breaks.has(i) ? null : prev
      forEachSegmentSpansTapered(segPrev, prevR, cur, r, emit, clip)
      prev = cur
      prevR = r
    }
    return finish()
  }
  for (let i = 0; i < n; i++) {
    const cur = sampleAt(payload.samples, i)
    const segPrev = breaks && breaks.has(i) ? null : prev
    if (lump) forEachSegmentSpansBanded(segPrev, cur, rFx, lump, emit, clip)
    else forEachSegmentSpans(segPrev, cur, rFx, emit, clip)
    prev = cur
  }
  return finish()
}

// ----------------------------------------------------------------------------
// Level
// ----------------------------------------------------------------------------

/**
 * Pours each footprint column: from the span's height (the level) down, fills empty cells until the
 * first occupied one. Without a readable target (dev assertions collecting keys) the spans are visited.
 */
function applyLevel(target: BrushTarget, spans: number[], value: VoxelData | undefined): number {
  const read = typeof (target as Partial<CellReader>).get === 'function' ? (target as unknown as CellReader) : null
  if (!read || !value) {
    let n = 0
    for (let i = 0; i < spans.length; i += 4) n += target.writeSpanX(spans[i], spans[i + 1], spans[i + 2], spans[i + 3], 'fill', value, undefined, undefined)
    return n
  }
  let n = 0
  for (let i = 0; i < spans.length; i += 4) {
    const top = spans[i], z = spans[i + 1], x0 = spans[i + 2], x1 = spans[i + 3]
    for (let x = x0; x <= x1; x++) {
      let y = top
      while (y >= 0 && read.get(posKeyXYZ(x, y, z)) === undefined) y--
      // Cells y + 1 .. top are empty: one vertical run, written cell by cell (spans run along x).
      for (let fy = y + 1; fy <= top; fy++) n += target.writeSpanX(fy, z, x, x, 'fill', value, undefined, undefined)
    }
  }
  return n
}

// ----------------------------------------------------------------------------
// Coat
// ----------------------------------------------------------------------------

/** Integer offsets within `coatFx` (fixed point) of the origin, nearest first, origin excluded. */
const coatOffsetCache = new Map<number, Int32Array>()
function coatOffsets(coatFx: number): Int32Array {
  let cached = coatOffsetCache.get(coatFx)
  if (cached) return cached
  const reach = Math.floor(coatFx / FX_ONE)
  const lim = coatFx * coatFx
  const list: [number, number, number, number][] = []
  for (let dz = -reach; dz <= reach; dz++) {
    for (let dy = -reach; dy <= reach; dy++) {
      for (let dx = -reach; dx <= reach; dx++) {
        const d2 = (dx * dx + dy * dy + dz * dz) * FX_ONE * FX_ONE
        if (d2 === 0 || d2 > lim) continue
        list.push([d2, dx, dy, dz])
      }
    }
  }
  list.sort((a, b) => a[0] - b[0] || a[2] - b[2] || a[3] - b[3] || a[1] - b[1])
  cached = new Int32Array(list.length * 3)
  list.forEach(([, dx, dy, dz], i) => { cached![i * 3] = dx; cached![i * 3 + 1] = dy; cached![i * 3 + 2] = dz })
  coatOffsetCache.set(coatFx, cached)
  return cached
}

const FACE_OFFSETS = [1, 0, 0, -1, 0, 0, 0, 1, 0, 0, -1, 0, 0, 0, 1, 0, 0, -1] as const

/**
 * The coat rule over the collected spans: fill empty cells within `coatFx` of the reference surface;
 * with `coatRepaint`, recolour occupied cells exposed in the reference (an empty face neighbour, or
 * a face on the bounds). Every decision reads the reference only, then writes. Without a reference
 * or a readable target (dev assertions collect visited keys), the spans are only visited.
 */
function applyCoat(
  target: BrushTarget,
  spans: number[],
  brush: BrushSnapshot,
  bounds: Bounds,
  keep: KeepTypes | undefined,
  reference: CellReader | null | undefined,
): number {
  const value = brush.value
  const ref = reference ?? (typeof (target as Partial<CellReader>).get === 'function' ? (target as unknown as CellReader) : null)
  if (!ref || !value) {
    let n = 0
    for (let i = 0; i < spans.length; i += 4) n += target.writeSpanX(spans[i], spans[i + 1], spans[i + 2], spans[i + 3], 'fill', value, undefined, undefined)
    return n
  }
  // Thickness 0: paint only, recolour the exposed surface and add nothing.
  const paintOnly = (brush.coatFx ?? FX_ONE) <= 0
  const offsets = coatOffsets(Math.max(FX_ONE, brush.coatFx ?? FX_ONE))
  const repaint = !!brush.coatRepaint
  const bx = bounds.x, by = bounds.y, bz = bounds.z
  const occupied = (x: number, y: number, z: number) =>
    x >= 0 && y >= 0 && z >= 0 && x < bx && y < by && z < bz && ref.get(posKeyXYZ(x, y, z)) !== undefined
  const nearSurface = (x: number, y: number, z: number) => {
    for (let i = 0; i < offsets.length; i += 3) if (occupied(x + offsets[i], y + offsets[i + 1], z + offsets[i + 2])) return true
    return false
  }
  const exposed = (x: number, y: number, z: number) => {
    for (let i = 0; i < 18; i += 3) {
      const nx = x + FACE_OFFSETS[i], ny = y + FACE_OFFSETS[i + 1], nz = z + FACE_OFFSETS[i + 2]
      if (nx < 0 || ny < 0 || nz < 0 || nx >= bx || ny >= by || nz >= bz) return true
      if (ref.get(posKeyXYZ(nx, ny, nz)) === undefined) return true
    }
    return false
  }
  // Decide every cell first (reads only), then write runs of fills and of recolours.
  const fills: number[] = []
  const recolours: number[] = []
  for (let i = 0; i < spans.length; i += 4) {
    const y = spans[i], z = spans[i + 1], x0 = spans[i + 2], x1 = spans[i + 3]
    let fillRun = Number.NaN, paintRun = Number.NaN
    for (let x = x0; x <= x1 + 1; x++) {
      let fill = false, paint = false
      if (x <= x1) {
        const old = ref.get(posKeyXYZ(x, y, z))
        if (old === undefined) fill = !paintOnly && nearSurface(x, y, z)
        else if (repaint && !(keep && isKept(old, keep)) && exposed(x, y, z)) paint = true
      }
      if (fill) { if (Number.isNaN(fillRun)) fillRun = x } else if (!Number.isNaN(fillRun)) { fills.push(y, z, fillRun, x - 1); fillRun = Number.NaN }
      if (paint) { if (Number.isNaN(paintRun)) paintRun = x } else if (!Number.isNaN(paintRun)) { recolours.push(y, z, paintRun, x - 1); paintRun = Number.NaN }
    }
  }
  let n = 0
  for (let i = 0; i < fills.length; i += 4) n += target.writeSpanX(fills[i], fills[i + 1], fills[i + 2], fills[i + 3], 'fill', value, undefined, undefined)
  for (let i = 0; i < recolours.length; i += 4) n += target.writeSpanX(recolours[i], recolours[i + 1], recolours[i + 2], recolours[i + 3], 'place', value, undefined, keep)
  return n
}

/** Cell-space bounding box of everything a stroke can touch (unclipped, mirrors excluded). */
export function brushStrokeCellBounds(payload: BrushStrokePayload): { min: Bounds; max: Bounds } | null {
  const n = sampleCount(payload)
  if (n === 0) return null
  const r = payload.brush.rFx
  let minX = Infinity, minY = Infinity, minZ = Infinity
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity
  const take = (x: number, y: number, z: number) => {
    if (x - r < minX) minX = x - r; if (x + r > maxX) maxX = x + r
    if (y - r < minY) minY = y - r; if (y + r > maxY) maxY = y + r
    if (z - r < minZ) minZ = z - r; if (z + r > maxZ) maxZ = z + r
  }
  for (let i = 0; i < n; i++) take(payload.samples[i * 3], payload.samples[i * 3 + 1], payload.samples[i * 3 + 2])
  if (payload.prev && payload.prev.length >= 3) take(payload.prev[0], payload.prev[1], payload.prev[2])
  return {
    min: { x: Math.ceil(minX / FX_ONE), y: Math.ceil(minY / FX_ONE), z: Math.ceil(minZ / FX_ONE) },
    max: { x: Math.floor(maxX / FX_ONE), y: Math.floor(maxY / FX_ONE), z: Math.floor(maxZ / FX_ONE) },
  }
}

// ----------------------------------------------------------------------------
// Targets
// ----------------------------------------------------------------------------

/**
 * Adapter for VoxelMap bases (grid transient or native Map). Writes every cell through
 * the plain writers so journal candidates are recorded exactly like the legacy scribble path.
 * Mirrors are handled by applyBrushStroke (span reflection), so no mirror context is consulted here.
 */
export function voxelMapBrushTarget(map: VoxelMap): BrushTarget & CellReader {
  return {
    get: (key) => map.get(key),
    writeSpanX(y, z, x0, x1, verb, value, scope, keep) {
      if (keep && (verb === 'subtract' || verb === 'subtractIf')) {
        if (verb === 'subtractIf' && !scope) return 0
        for (let x = x0; x <= x1; x++) writeSubtractKeeping(map, posKeyXYZ(x, y, z), keep, verb === 'subtractIf' ? scope : undefined)
        return x1 - x0 + 1
      }
      switch (verb) {
        case 'place':
          if (keep) {
            for (let x = x0; x <= x1; x++) {
              const key = posKeyXYZ(x, y, z)
              if (!isKept(map.get(key), keep)) writePlace(map, key, value as VoxelData)
            }
          } else {
            for (let x = x0; x <= x1; x++) writePlace(map, posKeyXYZ(x, y, z), value as VoxelData)
          }
          break
        case 'coat': // applyCoat / applyLevel resolve these to fill / place; a bare span only fills
        case 'level':
        case 'fill':
          for (let x = x0; x <= x1; x++) writeFill(map, posKeyXYZ(x, y, z), value as VoxelData)
          break
        case 'subtract':
          for (let x = x0; x <= x1; x++) writeSubtract(map, posKeyXYZ(x, y, z))
          break
        case 'subtractIf':
          if (!scope) return 0
          for (let x = x0; x <= x1; x++) writeSubtractIf(map, posKeyXYZ(x, y, z), scope)
          break
        case 'repaintIf':
          if (!scope) return 0
          for (let x = x0; x <= x1; x++) writeRepaintIf(map, posKeyXYZ(x, y, z), value as VoxelData, scope)
          break
      }
      return x1 - x0 + 1
    },
  }
}

/**
 * Span adapter for a mutable `ChunkedVoxelGrid`: maps each verb to a grid `SpanOp` (scoped verbs to
 * `subtractIf` / `repaintIf` with a palette-id mask of the scope's canonical type). Same per-cell
 * semantics as `voxelMapBrushTarget`; the reducer still writes through the Map adapter because the
 * candidate journal (native Map bases) needs per-cell candidates.
 */
export function chunkedGridBrushTarget(grid: ChunkedVoxelGrid): BrushTarget & CellReader {
  const palette = grid.palette
  let maskType: string | null = null
  let mask: Uint8Array | null = null
  const maskFor = (scope: BrushScope): Uint8Array => {
    if (mask && maskType === scope.onlyType && mask.length === palette.length) return mask
    const next = new Uint8Array(palette.length)
    const items = palette.items
    for (let id = 1; id < next.length; id++) {
      const item = items[id]
      if (item && canonicalVoxelType(item.type) === scope.onlyType) next[id] = 1
    }
    maskType = scope.onlyType
    mask = next
    return next
  }
  // Erasing around protected types: a palette-id mask of the cells the erase may remove (in scope,
  // when scoped, and not protected). Cached per (scope, keep) and palette size.
  let keepKey: string | null = null
  let keepMask: Uint8Array | null = null
  const keepMaskFor = (keep: KeepTypes, scope: BrushScope | undefined): Uint8Array => {
    const key = `${scope?.onlyType ?? ''}|${[...keep].join(',')}`
    if (keepMask && keepKey === key && keepMask.length === palette.length) return keepMask
    const next = new Uint8Array(palette.length)
    const items = palette.items
    for (let id = 1; id < next.length; id++) {
      const item = items[id]
      if (!item) continue
      const type = canonicalVoxelType(item.type)
      if (keep.has(type)) continue
      if (scope && type !== scope.onlyType) continue
      next[id] = 1
    }
    keepKey = key
    keepMask = next
    return next
  }
  return {
    get: (key) => grid.get(key),
    writeSpanX(y, z, x0, x1, verb, value, scope, keep) {
      let op: SpanOp
      switch (verb) {
        case 'place':
          op = keep
            ? { kind: 'accrete', id: palette.intern(value as VoxelData), canOverwrite: keepMaskFor(keep, undefined) }
            : { kind: 'place', id: palette.intern(value as VoxelData) }
          break
        case 'coat':
        case 'level':
        case 'fill': op = { kind: 'fill', id: palette.intern(value as VoxelData) }; break
        case 'subtract':
          op = keep ? { kind: 'subtractIf', canWrite: keepMaskFor(keep, undefined) } : { kind: 'subtract' }
          break
        case 'subtractIf':
          if (!scope) return 0
          op = { kind: 'subtractIf', canWrite: keep ? keepMaskFor(keep, scope) : maskFor(scope) }
          break
        case 'repaintIf': {
          if (!scope) return 0
          const id = palette.intern(value as VoxelData)
          op = { kind: 'repaintIf', id, canWrite: maskFor(scope) }
          break
        }
      }
      grid.writeSpanX(y, z, x0, x1, op)
      return x1 - x0 + 1
    },
  }
}

/** A target that only collects visited keys (tests, dev assertions, previews). */
export function keySetBrushTarget(out: Set<number> = new Set()): BrushTarget & { keys: Set<number> } {
  return {
    keys: out,
    writeSpanX(y, z, x0, x1) {
      for (let x = x0; x <= x1; x++) out.add(posKeyXYZ(x, y, z))
      return x1 - x0 + 1
    },
  }
}
